import classNames from "classnames";
import {Fragment, ReactNode, useEffect, useState} from "react";
import {useFormContext} from "react-hook-form";

import {Alert} from "@/components/alert/Alert.tsx";
import {useCurrentUserContext} from "@/components/context/hooks.ts";
import {Divider} from "@/components/divider/Divider.tsx";
import {Section} from "@/components/section/Section.tsx";
import {AutoLocationServiceTypes, ServiceField} from "@/lib/constants/service.ts";
import {MonitoringServiceChoices, MonitoringServiceType, ServiceDefinition, ServiceTag} from "@/lib/types/checks.ts";
import {MonitoringGroup} from "@/lib/types/monitoring.ts";
import {CheckFormAutoGeneratedField} from "@/pages/checks/components/form/fields/CheckFormAutoGeneratedField.tsx";
import {
  AutogeneratedServiceFields,
  FIELD_TYPES,
  noWrapFields,
  sensitivityAndTargetingFields,
} from "@/pages/checks/constants.ts";
import {
  fieldIsEnabled,
  hasTestRunnerOnSensitivity,
  isLocationFieldVisible,
  sectionHasChanges,
} from "@/pages/checks/utils.ts";

import styles from "./CheckDetailsForm.module.scss";

interface CheckSensitivityAndTargetingFormProps {
  serviceType: MonitoringServiceType;
  service: ServiceDefinition;
  mspChoices: MonitoringServiceChoices;
  monitoringGroups: MonitoringGroup[];
  tags?: ServiceTag[];
  testRunnerForm?: ReactNode;
}

export const CheckSensitivityAndTargetingForm = ({
  serviceType,
  service,
  mspChoices,
  monitoringGroups,
  testRunnerForm,
}: CheckSensitivityAndTargetingFormProps) => {
  const {
    watch,
    formState: {defaultValues, errors},
  } = useFormContext();
  const {user, userHasFeature} = useCurrentUserContext();
  const [advancedFormWarnings, setAdvancedFormWarnings] = useState<Record<string, ReactNode | null>>({});
  const [isCollapsibleForcedOpen, setIsCollapsibleForcedOpen] = useState(false);
  const sensitivity = watch(ServiceField.SENSITIVITY);
  const locations = watch(ServiceField.LOCATION);
  const numRetries = watch(ServiceField.NUM_RETRIES);
  const threshold = watch(ServiceField.THRESHOLD);
  const isExtendedTransactionsEnabled =
    serviceType === MonitoringServiceType.TRANSACTION && user!.account.enable_transaction_ext_checks;
  const isExtendedTransaction = isExtendedTransactionsEnabled && threshold && Number(threshold) > 60;
  const isEditing = !!watch(ServiceField.ID);
  const originalExternalDomains = (defaultValues || {})[ServiceField.RUM_EXTERNAL_DOMAINS] || "";
  const externalDomains = watch(ServiceField.RUM_EXTERNAL_DOMAINS) || "";
  const originalAjaxDisabled = (defaultValues || {})[ServiceField.RUM_IS_AJAX_DISABLED] || false;
  const ajaxDisabled = watch(ServiceField.RUM_IS_AJAX_DISABLED) || false;

  useEffect(() => {
    let sensitivityWarning = null;
    if (!AutoLocationServiceTypes.includes(serviceType)) {
      if (!isExtendedTransaction && sensitivity && Number(sensitivity) < 2) {
        sensitivityWarning = (
          <Alert
            callout="Attention"
            children="We recommend that Check sensitivity is set to 2 or more locations."
            variant="maintenance"
            className="mb-0 mt-3 pulseMaxWidthXL pulseFullRow"
          />
        );
      } else if (
        sensitivity &&
        locations &&
        isLocationFieldVisible(monitoringGroups, service.force_monitoring_group_type) &&
        Number(sensitivity) >= Math.max(locations.length, 2)
      ) {
        sensitivityWarning = (
          <Alert
            callout="Attention"
            children="We recommend that Check sensitivity is set to less than the number of locations."
            variant="maintenance"
            className="mb-0 mt-3 pulseMaxWidthXL pulseFullRow"
          />
        );
      }
    }

    let retriesWarning = null;
    if (numRetries && Number(numRetries) <= 1) {
      retriesWarning = (
        <Alert
          callout="Attention"
          children="We recommend using 2 retries for fast Alert times that avoid false positives."
          variant="maintenance"
          className="mb-0 mt-3 pulseMaxWidthXL pulseFullRow"
        />
      );
    }

    let rumExternalDomainsWarning = null;
    let rumAjaxDisabledWarning = null;
    if (isEditing) {
      if (originalExternalDomains !== externalDomains) {
        rumExternalDomainsWarning = (
          <Alert
            callout="Attention"
            children='You must update your snippet for these changes to take effect. "Allowed External Domains" was modified, so the snippet has changed.'
            variant="maintenance"
            className="mb-0 mt-3 pulseMaxWidthL pulseFullRow"
          />
        );
      }
      if (originalAjaxDisabled !== ajaxDisabled) {
        rumAjaxDisabledWarning = (
          <Alert
            callout="Attention"
            children='You must update your snippet for these changes to take effect. "Disable AJAX Tracking" was modified, so the snippet has changed.'
            variant="maintenance"
            className="mb-0 mt-3 pulseMaxWidthL pulseFullRow"
          />
        );
      }
    }

    setAdvancedFormWarnings({
      [ServiceField.SENSITIVITY]: sensitivityWarning,
      [ServiceField.NUM_RETRIES]: retriesWarning,
      [ServiceField.RUM_EXTERNAL_DOMAINS]: rumExternalDomainsWarning,
      [ServiceField.RUM_IS_AJAX_DISABLED]: rumAjaxDisabledWarning,
    });
  }, [
    locations,
    monitoringGroups,
    numRetries,
    sensitivity,
    service.force_monitoring_group_type,
    serviceType,
    isExtendedTransaction,
    isEditing,
    originalExternalDomains,
    externalDomains,
    originalAjaxDisabled,
    ajaxDisabled,
  ]);

  const serviceFieldsMap = service.fields.reduce(
    (acc, field) => {
      acc[field.field as ServiceField] = field;
      return acc;
    },
    {} as Record<ServiceField, (typeof service.fields)[number]>
  );

  const sections = (sensitivityAndTargetingFields[serviceType as keyof typeof sensitivityAndTargetingFields] ?? [])
    .map((section) => ({
      ...section,
      rows: section.rows
        .map((row) => ({
          ...row,
          fields: row.fields.filter((fieldObj) => fieldIsEnabled(serviceFieldsMap[fieldObj.field], userHasFeature)),
        }))
        .filter((row) => row.fields.length > 0),
    }))
    .filter((section) => section.rows.length > 0);

  const collapsibleFields = sections
    .filter((section) => "collapsible" in section && section.collapsible)
    .flatMap((section) =>
      section.rows.flatMap((row) => row.fields.map((fieldObj) => serviceFieldsMap[fieldObj.field]))
    );
  const hasCollapsibleErrors = collapsibleFields.some((field) => field.field in errors);
  const isCollapsibleOpen =
    isCollapsibleForcedOpen || sectionHasChanges(collapsibleFields, defaultValues ?? {}, errors);

  useEffect(() => {
    if (hasCollapsibleErrors) {
      setIsCollapsibleForcedOpen(true);
    }
  }, [hasCollapsibleErrors]);

  const renderedSections = sections.map((section, index) => {
    const rows = section.rows.map((row, rowIndex) => (
      <Fragment key={rowIndex}>
        <div
          className={classNames("mt-4 d-flex gap-4 gap-sm-3 flex-column flex-sm-row", {
            [`pulseMaxWidth${row.size}`]: row.size,
          })}
        >
          {row.fields.map((fieldObj) => (
            <div
              key={fieldObj.field}
              className={classNames("col smBreakpointPulseMaxWidthXL", {
                [`pulseMaxWidth${fieldObj.size}`]: fieldObj.size,
                [styles.noWrap]: noWrapFields.includes(fieldObj.field),
              })}
            >
              <CheckFormAutoGeneratedField
                field={serviceFieldsMap[fieldObj.field]}
                versions={service.versions}
                serviceType={service.service_type}
                type={FIELD_TYPES[fieldObj.field as AutogeneratedServiceFields]}
                mspChoices={mspChoices}
              />
            </div>
          ))}
        </div>

        {"warnings" in row &&
          row.warnings?.map(
            (field) =>
              advancedFormWarnings[field] && (
                <div
                  key={field}
                  className={classNames("col smBreakpointPulseMaxWidthXL", {
                    [`pulseMaxWidth${row.size}`]: row.size,
                  })}
                >
                  {advancedFormWarnings[field]}
                </div>
              )
          )}
      </Fragment>
    ));
    const sectionClassName = classNames({"mt-4 pt-2": index !== 0});

    if ("collapsible" in section && section.collapsible) {
      return (
        <Section
          key={`${section.section}-${isCollapsibleForcedOpen}`}
          title={section.title}
          className={sectionClassName}
          defaultOpen={isCollapsibleOpen}
        >
          {rows}
        </Section>
      );
    }
    return (
      <Fragment key={section.section}>
        <Section title={section.title} className={sectionClassName} />
        {rows}
      </Fragment>
    );
  });

  return (
    <div className="d-flex gap-4">
      <div className="pulseMaxWidthL w-100">
        {renderedSections}
        {testRunnerForm && hasTestRunnerOnSensitivity(serviceType) && (
          <div className="d-md-none mt-4">{testRunnerForm}</div>
        )}
      </div>
      {testRunnerForm && hasTestRunnerOnSensitivity(serviceType) && (
        <>
          <div className="d-none d-md-block">
            <Divider orientation="vertical" className="mx-2" />
          </div>
          <div className={classNames(styles.testRunner, "d-none d-md-block")}>{testRunnerForm}</div>
        </>
      )}
    </div>
  );
};
